import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { PublicFooter } from "@/components/legal/public-footer";
import { multiResumeBrand } from "@/utils/multiresume-brand";

import { GuideStructuredData } from "./guide-structured-data";
import {
  CHECK_AI_RESUME_EDITS_GUIDE_PATH,
  GUIDES_DIRECTORY_CANONICAL_URL,
  guidesDirectoryMessages,
  MANAGE_RESUME_VERSIONS_GUIDE_PATH,
  checkAiResumeEditsGuideTitle,
  TAILOR_RESUME_GUIDE_PATH,
} from "./guides-copy";

export const checkAiResumeEditsMessages = {
  title: checkAiResumeEditsGuideTitle,
  metadataTitle: msg({
    id: "growth.c03.metadata-title",
    message: "How to fact-check AI resume edits",
  }),
  metadataDescription: msg({
    id: "growth.c03.metadata-description",
    message:
      "Check an AI-edited resume against your own records. Work through an example to catch invented numbers, overstated responsibilities, and missing context.",
  }),
  intro: msg({
    id: "growth.c03.intro",
    message:
      "“Helped prepare weekly status reports” becomes “managed project delivery.” The second phrase sounds stronger on a resume. If you collected updates but didn't assign work or manage the schedule, it also describes a different job.\n\nAn AI rewrite can change a claim without adding an obvious new number. It may drop “assisted,” turn a short project into an ongoing responsibility, or make a team's result sound like your individual achievement. A smooth read won't catch all of that.\n\nBefore you submit an AI-edited resume, compare each changed claim with your own records. Check responsibilities as well as names, dates, and numbers, then decide what to keep, correct, or leave out until you can verify it.",
  }),
  onThisPage: msg({ id: "growth.c03.on-this-page", message: "On this page" }),
  tocOne: msg({ id: "growth.c03.toc-one", message: "Read the rewrite beside the original facts" }),
  tocTwo: msg({ id: "growth.c03.toc-two", message: "Review the changes separately" }),
  tocThree: msg({ id: "growth.c03.toc-three", message: "Ask for a specific correction, then check it again" }),
  tocFour: msg({ id: "growth.c03.toc-four", message: "Check what disappeared as well as what was added" }),
  sectionOneHeading: msg({
    id: "growth.c03.section-one-heading",
    message: "1. Read the rewrite beside the original facts",
  }),
  sectionOneOpening: msg({
    id: "growth.c03.section-one-opening",
    message: "Consider this fictional experience:",
  }),
  originalExperience: msg({
    id: "growth.c03.original-experience",
    message: "Helped a four-person project team prepare weekly status reports for six months.",
  }),
  rewrittenExperience: msg({
    id: "growth.c03.rewritten-experience",
    message: "Led a 10-person global program and reduced costs by 30%.",
  }),
  originalExperienceLabel: msg({ id: "growth.c03.original-experience-label", message: "Original experience" }),
  aiRewriteLabel: msg({ id: "growth.c03.ai-rewrite-label", message: "AI rewrite" }),
  sectionOneAfterComparison: msg({
    id: "growth.c03.section-one-after-comparison",
    message:
      "Both sentences were written for this example. They aren't a real applicant's record or the result of testing a particular AI tool.\n\nThe original supports a small set of facts: the candidate helped the team, there were four people, the task involved weekly status reports, and the work lasted six months. It doesn't establish leadership, international scope, a team of ten, or cost savings. None of those additions is ready to use.\n\nFor your own resume, check project notes, employment records, or work summaries you're allowed to access. Your old resume may omit something you genuinely did. Confirm that missing detail before adding it. The job description can tell you what the employer wants; it can't establish that you have done it.\n\nYou don't need to upload internal documents to make this check. Read them yourself and give the tool only the relevant facts you're permitted to share. Keep client details, confidential business data, and other people's contact information out of the prompt.",
  }),
  sectionTwoHeading: msg({ id: "growth.c03.section-two-heading", message: "2. Review the changes separately" }),
  sectionTwoOpening: msg({
    id: "growth.c03.section-two-opening",
    message:
      "The invented rewrite above is an obvious problem. A real draft may contain one overstatement inside an otherwise useful sentence. You can keep an accurate edit without accepting everything around it.\n\nThese possible edits all refer back to the same fictional source sentence:",
  }),
  tableHeaderOriginal: msg({ id: "growth.c03.table-header-original", message: "Original information" }),
  tableHeaderProposed: msg({ id: "growth.c03.table-header-proposed", message: "Proposed wording" }),
  tableHeaderSupports: msg({ id: "growth.c03.table-header-supports", message: "What the source supports" }),
  tableHeaderDecision: msg({ id: "growth.c03.table-header-decision", message: "Decision" }),
  tableRowOneOriginal: msg({ id: "growth.c03.table-row-one-original", message: "Prepare weekly status reports" }),
  tableRowOneProposed: msg({ id: "growth.c03.table-row-one-proposed", message: "Compile weekly project updates" }),
  tableRowOneSupports: msg({
    id: "growth.c03.table-row-one-supports",
    message: "A shorter description of the same reporting task",
  }),
  tableRowOneDecision: msg({ id: "growth.c03.table-row-one-decision", message: "Keep this wording change" }),
  tableRowTwoOriginal: msg({ id: "growth.c03.table-row-two-original", message: "Help the team prepare reports" }),
  tableRowTwoProposed: msg({ id: "growth.c03.table-row-two-proposed", message: "Manage project delivery" }),
  tableRowTwoSupports: msg({
    id: "growth.c03.table-row-two-supports",
    message: "Preparing reports doesn't establish responsibility for assignments or schedules",
  }),
  tableRowTwoDecision: msg({
    id: "growth.c03.table-row-two-decision",
    message: "Restore the support role and actual task",
  }),
  tableRowThreeOriginal: msg({
    id: "growth.c03.table-row-three-original",
    message: "Four-person team; no geographic scope supplied",
  }),
  tableRowThreeProposed: msg({ id: "growth.c03.table-row-three-proposed", message: "Lead a 10-person global team" }),
  tableRowThreeSupports: msg({
    id: "growth.c03.table-row-three-supports",
    message: "Leadership, a different headcount, and international scope are all unsupported",
  }),
  tableRowThreeDecision: msg({
    id: "growth.c03.table-row-three-decision",
    message: "Reject the additions and restore the known facts",
  }),
  tableRowFourOriginal: msg({ id: "growth.c03.table-row-four-original", message: "Work continued for six months" }),
  tableRowFourProposed: msg({
    id: "growth.c03.table-row-four-proposed",
    message: "Deliver the project on time within six months",
  }),
  tableRowFourSupports: msg({
    id: "growth.c03.table-row-four-supports",
    message: "Time spent doing a task doesn't prove project completion or an on-time delivery",
  }),
  tableRowFourDecision: msg({
    id: "growth.c03.table-row-four-decision",
    message: "Remove the delivery claim; keep the duration",
  }),
  tableRowFiveOriginal: msg({ id: "growth.c03.table-row-five-original", message: "No cost-saving data supplied" }),
  tableRowFiveProposed: msg({ id: "growth.c03.table-row-five-proposed", message: "Reduce costs by 30%" }),
  tableRowFiveSupports: msg({
    id: "growth.c03.table-row-five-supports",
    message: "Neither the number nor a cost reduction is established",
  }),
  tableRowFiveDecision: msg({
    id: "growth.c03.table-row-five-decision",
    message: "Leave it out; don't substitute a guessed figure",
  }),
  sectionTwoBeforeVersion: msg({
    id: "growth.c03.section-two-before-version",
    message: "With only the supplied facts, a usable version is:",
  }),
  usableVersion: msg({
    id: "growth.c03.usable-version",
    message: "Assisted a four-person project team with weekly status reports over six months.",
  }),
  sectionTwoAfterVersion: msg({
    id: "growth.c03.section-two-after-version",
    message:
      "That retains the task, supporting role, team size, and duration. To make it more specific, you'd need answers to questions such as what the reports covered, which part you handled, and who used them. Add those details only after confirming them.\n\nA claim you can't verify yet isn't necessarily false. It still shouldn't go into the version you're about to submit. If you remember helping improve a process but can't substantiate the time saved, describe the confirmed work without attaching an unverified result.\n\nChanging “reduced costs by 30%” to “significantly reduced costs” doesn't fix this example. The number has gone, but the cost-saving claim still needs evidence.",
  }),
  sectionThreeHeading: msg({
    id: "growth.c03.section-three-heading",
    message: "3. Ask for a specific correction, then check it again",
  }),
  sectionThreeOpening: msg({
    id: "growth.c03.section-three-opening",
    message:
      "Select the sentence that needs repair and explain which facts are confirmed. Keeping the request narrow makes it easier to see what changed and avoids another full-resume review just to fix one line.\n\nFor the example above, you could use:",
  }),
  correctionPrompt: msg({
    id: "growth.c03.correction-prompt",
    message:
      "Revise only this sentence:\n“Led a 10-person global program and reduced costs by 30%.”\n\nFacts you may use:\nI helped a four-person project team prepare weekly status reports\nfor six months.\n\nKeep the supporting role, four-person team, and six-month duration.\nDo not add leadership, international scope, cost savings, or on-time\ndelivery. None of those was supplied.\nGive the revised sentence, followed separately by the original facts\nit relies on. If a detail is missing, ask me instead of filling it in.",
  }),
  sectionThreeAfterPrompt: msg({
    id: "growth.c03.section-three-after-prompt",
    message:
      "This request explains the limits; it doesn't guarantee an accurate answer. If the tool lists a source for its wording, check that the detail is actually in your material.\n\nCompare the returned sentence with the original facts, not just with the previous AI draft. Otherwise you may notice that “30%” has disappeared but miss “led,” or a new claim of independent responsibility.",
  }),
  sectionThreeC01Lead: msg({
    id: "growth.c03.section-three-c01-lead",
    message: "If the problem is that you've chosen the wrong project for the role, use",
  }),
  c01LinkText: msg({ id: "growth.c03.c01-link-text", message: "the guide to tailoring a resume to a job description" }),
  sectionThreeC01Suffix: msg({
    id: "growth.c03.section-three-c01-suffix",
    message:
      ". Choosing what belongs on the page is a separate decision from checking whether an existing sentence is accurate.",
  }),
  sectionFourHeading: msg({
    id: "growth.c03.section-four-heading",
    message: "4. Check what disappeared as well as what was added",
  }),
  sectionFourOpening: msg({
    id: "growth.c03.section-four-opening",
    message:
      "Omissions can broaden a claim. “Assisted” disappears. “Some clients” becomes “clients.” A six-month assignment is absorbed into the regular duties of a job held for several years. The resulting sentence may imply more responsibility or a longer period than the original.\n\nAfter reviewing the individual edits, read the whole resume with these questions in mind:",
  }),
  checklistOne: msg({
    id: "growth.c03.checklist-one",
    message:
      "Are employer names, actual job titles, dates, and education accurate? Did an unsupported skill, certification, or proficiency level appear in the summary or skills section?",
  }),
  checklistTwo: msg({
    id: "growth.c03.checklist-two",
    message:
      "Are qualifications such as “assisted,” “part of,” and “selected clients” still present where they matter? Did team size become a number of direct reports, or a team result become something you accomplished alone?",
  }),
  checklistThree: msg({
    id: "growth.c03.checklist-three",
    message:
      "Does each number still refer to the same measure, period, and group? Two events happening in sequence aren't enough to show that your work caused the result.",
  }),
  checklistFour: msg({
    id: "growth.c03.checklist-four",
    message:
      "Did the revision remove a concrete contribution and leave only vague language such as “supported the business”? Restore accurate details; avoiding overstatement doesn't require deleting the work you did.",
  }),
  sectionFourClosing: msg({
    id: "growth.c03.section-four-closing",
    message:
      "Take the two or three sentences that changed most and try answering an interviewer's follow-up questions: What did you personally do? How was that number calculated? What did other people handle?\n\nBeing able to explain a sentence fluently doesn't verify it. But if answering requires inventing a step you never took or avoiding the origin of a number, the sentence isn't ready. Check your records, remove the unsupported part or return to the confirmed wording, and save the reviewed text in the relevant working resume.\n\nFor keeping that reviewed text with the right application version, see",
  }),
  c02LinkText: msg({ id: "growth.c03.c02-link-text", message: "the guide to managing multiple resume versions" }),
  productCall: msg({
    id: "growth.c03.product-call",
    message:
      "In MultiResume, you review changes and decide how to handle them. That doesn't mean the AI output has been certified as factually correct.",
  }),
  productHomeLink: msg({
    id: "growth.c03.product-home-link",
    message: "Learn about the Complete Resume and Job Version workflow.",
  }),
  productAccess: msg({
    id: "growth.c03.product-access",
    message: "Access is currently invitation-only; you don't need an account to read this guide.",
  }),
} as const;

const tableHeaders = [
  checkAiResumeEditsMessages.tableHeaderOriginal,
  checkAiResumeEditsMessages.tableHeaderProposed,
  checkAiResumeEditsMessages.tableHeaderSupports,
  checkAiResumeEditsMessages.tableHeaderDecision,
] as const;

const tableRows = [
  [
    checkAiResumeEditsMessages.tableRowOneOriginal,
    checkAiResumeEditsMessages.tableRowOneProposed,
    checkAiResumeEditsMessages.tableRowOneSupports,
    checkAiResumeEditsMessages.tableRowOneDecision,
  ],
  [
    checkAiResumeEditsMessages.tableRowTwoOriginal,
    checkAiResumeEditsMessages.tableRowTwoProposed,
    checkAiResumeEditsMessages.tableRowTwoSupports,
    checkAiResumeEditsMessages.tableRowTwoDecision,
  ],
  [
    checkAiResumeEditsMessages.tableRowThreeOriginal,
    checkAiResumeEditsMessages.tableRowThreeProposed,
    checkAiResumeEditsMessages.tableRowThreeSupports,
    checkAiResumeEditsMessages.tableRowThreeDecision,
  ],
  [
    checkAiResumeEditsMessages.tableRowFourOriginal,
    checkAiResumeEditsMessages.tableRowFourProposed,
    checkAiResumeEditsMessages.tableRowFourSupports,
    checkAiResumeEditsMessages.tableRowFourDecision,
  ],
  [
    checkAiResumeEditsMessages.tableRowFiveOriginal,
    checkAiResumeEditsMessages.tableRowFiveProposed,
    checkAiResumeEditsMessages.tableRowFiveSupports,
    checkAiResumeEditsMessages.tableRowFiveDecision,
  ],
] as const;

const tocItems = [
  ["read-original-facts", checkAiResumeEditsMessages.tocOne],
  ["review-changes-separately", checkAiResumeEditsMessages.tocTwo],
  ["ask-specific-correction", checkAiResumeEditsMessages.tocThree],
  ["check-disappeared-content", checkAiResumeEditsMessages.tocFour],
] as const;

function messageText(currentI18n: typeof i18n, message: MessageDescriptor) {
  return currentI18n._(message);
}

function TextBlocks({ currentI18n, message }: { currentI18n: typeof i18n; message: MessageDescriptor }) {
  return messageText(currentI18n, message)
    .split(/\n\n+/)
    .map((paragraph, index) => <p key={`${message.id}-${index}`}>{paragraph}</p>);
}

function LinkedTextBlocks({
  currentI18n,
  message,
  href,
  entry,
  linkText,
  suffix,
}: {
  currentI18n: typeof i18n;
  message: MessageDescriptor;
  href: string;
  entry: string;
  linkText: MessageDescriptor;
  suffix?: MessageDescriptor;
}) {
  const paragraphs = messageText(currentI18n, message).split(/\n\n+/);
  const finalParagraph = paragraphs.at(-1) ?? "";

  return (
    <>
      {paragraphs.slice(0, -1).map((paragraph, index) => (
        <p key={`${message.id}-${index}`}>{paragraph}</p>
      ))}
      <p>
        {finalParagraph}{" "}
        <a className="font-medium text-foreground underline underline-offset-4" href={href} data-phase6-entry={entry}>
          {messageText(currentI18n, linkText)}
        </a>
        {suffix ? messageText(currentI18n, suffix) : null}
      </p>
    </>
  );
}

function GuideSection({
  currentI18n,
  id,
  heading,
  children,
}: {
  currentI18n: typeof i18n;
  id: string;
  heading: MessageDescriptor;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 space-y-6">
      <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {messageText(currentI18n, heading)}
      </h2>
      {children}
    </section>
  );
}

function FactReviewTable({ currentI18n }: { currentI18n: typeof i18n }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="hidden bg-muted/30 text-xs font-semibold tracking-wide text-muted-foreground uppercase md:table-header-group">
          <tr>
            {tableHeaders.map((header) => (
              <th key={header.id} className="p-4 align-top">
                {messageText(currentI18n, header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr key={row[0].id} className="block border-t border-border first:border-t-0 md:table-row">
              {row.map((cell, index) => (
                <td key={cell.id} className="block p-4 align-top md:table-cell">
                  <span className="mb-1 block text-xs font-semibold tracking-wide text-muted-foreground uppercase md:hidden">
                    {messageText(currentI18n, tableHeaders[index])}
                  </span>
                  <span className="break-words">{messageText(currentI18n, cell)}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function getCheckAiResumeEditsGuideMetadata() {
  const title = messageText(i18n, checkAiResumeEditsMessages.metadataTitle);
  const description = messageText(i18n, checkAiResumeEditsMessages.metadataDescription);

  return {
    title: `${title} - ${multiResumeBrand.appName}`,
    description,
    ogUrl: `${GUIDES_DIRECTORY_CANONICAL_URL}/check-ai-resume-edits`,
  };
}

export function CheckAiResumeEditsGuide() {
  const { i18n: currentI18n } = useLingui();
  const t = (message: MessageDescriptor) => messageText(currentI18n, message);

  return (
    <main id="main-content" className="min-h-svh bg-background pt-20">
      <GuideStructuredData
        headline={t(checkAiResumeEditsMessages.title)}
        description={t(checkAiResumeEditsMessages.metadataDescription)}
        url={`${GUIDES_DIRECTORY_CANONICAL_URL}/check-ai-resume-edits`}
        language={currentI18n.locale}
        directoryName={t(guidesDirectoryMessages.title)}
      />
      <article className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-12 lg:py-16">
        <nav aria-label={t(guidesDirectoryMessages.breadcrumb)} className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <li>
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href="/guides"
                data-phase6-entry="article-to-guides"
              >
                {t(guidesDirectoryMessages.title)}
              </a>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{t(checkAiResumeEditsMessages.title)}</li>
          </ol>
        </nav>

        <header className="space-y-5 border-b border-border pb-10">
          <h1 className="text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl">
            {t(checkAiResumeEditsMessages.title)}
          </h1>
          <div className="max-w-3xl space-y-4 text-base leading-relaxed text-muted-foreground">
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.intro} />
          </div>
        </header>

        <nav
          aria-label={t(checkAiResumeEditsMessages.onThisPage)}
          className="my-10 rounded-lg border border-border bg-muted/20 p-5"
        >
          <p className="text-sm font-semibold text-foreground">{t(checkAiResumeEditsMessages.onThisPage)}</p>
          <ol className="mt-3 space-y-2 text-sm">
            {tocItems.map(([id, message]) => (
              <li key={id}>
                <a
                  className="font-medium text-foreground underline decoration-border underline-offset-4"
                  href={`#${id}`}
                >
                  {t(message)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-16 text-base leading-relaxed text-muted-foreground">
          <GuideSection
            currentI18n={currentI18n}
            id="read-original-facts"
            heading={checkAiResumeEditsMessages.sectionOneHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionOneOpening} />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-5">
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t(checkAiResumeEditsMessages.originalExperienceLabel)}
                </p>
                <blockquote className="text-foreground">{t(checkAiResumeEditsMessages.originalExperience)}</blockquote>
              </div>
              <div className="space-y-3 rounded-lg border border-primary/40 bg-primary/5 p-5">
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t(checkAiResumeEditsMessages.aiRewriteLabel)}
                </p>
                <blockquote className="text-foreground">{t(checkAiResumeEditsMessages.rewrittenExperience)}</blockquote>
              </div>
            </div>
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionOneAfterComparison} />
          </GuideSection>

          <GuideSection
            currentI18n={currentI18n}
            id="review-changes-separately"
            heading={checkAiResumeEditsMessages.sectionTwoHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionTwoOpening} />
            <FactReviewTable currentI18n={currentI18n} />
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionTwoBeforeVersion} />
            <blockquote className="rounded-lg border border-primary/40 bg-primary/5 p-5 text-foreground">
              {t(checkAiResumeEditsMessages.usableVersion)}
            </blockquote>
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionTwoAfterVersion} />
          </GuideSection>

          <GuideSection
            currentI18n={currentI18n}
            id="ask-specific-correction"
            heading={checkAiResumeEditsMessages.sectionThreeHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionThreeOpening} />
            <pre className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-5 text-sm leading-relaxed whitespace-pre-wrap">
              {t(checkAiResumeEditsMessages.correctionPrompt)}
            </pre>
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionThreeAfterPrompt} />
            <LinkedTextBlocks
              currentI18n={currentI18n}
              message={checkAiResumeEditsMessages.sectionThreeC01Lead}
              href={TAILOR_RESUME_GUIDE_PATH}
              entry="c03-to-c01"
              linkText={checkAiResumeEditsMessages.c01LinkText}
              suffix={checkAiResumeEditsMessages.sectionThreeC01Suffix}
            />
          </GuideSection>

          <GuideSection
            currentI18n={currentI18n}
            id="check-disappeared-content"
            heading={checkAiResumeEditsMessages.sectionFourHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={checkAiResumeEditsMessages.sectionFourOpening} />
            <ul className="list-disc space-y-3 pl-5">
              {[
                checkAiResumeEditsMessages.checklistOne,
                checkAiResumeEditsMessages.checklistTwo,
                checkAiResumeEditsMessages.checklistThree,
                checkAiResumeEditsMessages.checklistFour,
              ].map((item) => (
                <li key={item.id}>{t(item)}</li>
              ))}
            </ul>
            <LinkedTextBlocks
              currentI18n={currentI18n}
              message={checkAiResumeEditsMessages.sectionFourClosing}
              href={MANAGE_RESUME_VERSIONS_GUIDE_PATH}
              entry="c03-to-c02"
              linkText={checkAiResumeEditsMessages.c02LinkText}
            />
            <p>
              {t(checkAiResumeEditsMessages.productCall)}{" "}
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href="/"
                data-phase6-entry="article-to-home"
              >
                {t(checkAiResumeEditsMessages.productHomeLink)}
              </a>{" "}
              {t(checkAiResumeEditsMessages.productAccess)}
            </p>
          </GuideSection>
        </div>
      </article>

      <PublicFooter />
    </main>
  );
}

export { CHECK_AI_RESUME_EDITS_GUIDE_PATH };
